// Image Rotation command — rotates the entire document by 90° CW/CCW,
// 180°, or an arbitrary angle. 90°/180° flips also swap canvas dimensions.

import type { Command } from "./Command";
import type { Document } from "../document/Document";
import type { RasterLayer } from "../document/Layer";
import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import { rotateOrientationBuffer, type OrientationOp } from "../pixel/orientationTransform";
import {
  rotateSelection90CW,
  rotateSelection90CCW,
  rotateSelection180,
  flipSelectionH,
  flipSelectionV,
} from "./SelectionTransformCommand";

export type RotationMode = "90-cw" | "90-ccw" | "180" | "arbitrary" | "flip-h" | "flip-v";

export interface RotateOptions {
  mode: RotationMode;
  /** Angle in degrees for "arbitrary" mode. */
  angle?: number;
}

export class RotateImageCommand implements Command {
  readonly kind = "rotate-image";

  constructor(
    private readonly options: RotateOptions,
    private before?: { width: number; height: number },
  ) {}

  apply(doc: Document): Document {
    const { mode } = this.options;

    if (!this.before) {
      this.before = { width: doc.width, height: doc.height };
    }

    for (const layer of collectRasters(doc.layers)) {
      rotateLayer(layer, mode, this.options.angle);
    }

    // #69: rotate doc.selection in sync with the canvas.
    let nextSel: Selection | null = doc.selection;
    switch (mode) {
      case "90-cw":
        nextSel = rotateSelection90CW(doc.selection);
        break;
      case "90-ccw":
        nextSel = rotateSelection90CCW(doc.selection);
        break;
      case "180":
        nextSel = rotateSelection180(doc.selection);
        break;
      case "flip-h":
        nextSel = flipSelectionH(doc.selection);
        break;
      case "flip-v":
        nextSel = flipSelectionV(doc.selection);
        break;
      case "arbitrary":
        // PS drops the selection for arbitrary rotation — the math is not
        // sensible at non-orthogonal angles for axis-aligned selection masks.
        nextSel = null;
        break;
    }
    (doc as { selection: Selection | null }).selection = nextSel;

    // Swap dimensions for 90° rotations
    if (mode === "90-cw" || mode === "90-ccw") {
      const tmp = doc.width;
      doc.width = doc.height;
      doc.height = tmp;
    }

    return doc;
  }

  invert(_before: Document, _after: Document): Command {
    if (!this.before) throw new Error("Cannot invert without before state");
    // Inverse rotation
    let invMode: RotationMode;
    switch (this.options.mode) {
      case "90-cw": invMode = "90-ccw"; break;
      case "90-ccw": invMode = "90-cw"; break;
      case "180": invMode = "180"; break;
      case "flip-h": invMode = "flip-h"; break;
      case "flip-v": invMode = "flip-v"; break;
      case "arbitrary": invMode = "arbitrary"; break;
      default: invMode = "90-cw";
    }
    return new RotateImageCommand(
      { mode: invMode, angle: this.options.angle ? -this.options.angle : 0 },
      { width: _after.width, height: _after.height },
    );
  }
}

function collectRasters(layers: readonly import("../document/Layer").Layer[]): import("../document/Layer").RasterLayer[] {
  const result: import("../document/Layer").RasterLayer[] = [];
  for (const l of layers) {
    if (l.kind === "raster") result.push(l);
    else if (l.kind === "group") result.push(...collectRasters(l.children));
  }
  return result;
}

function rotateLayer(
  layer: import("../document/Layer").RasterLayer,
  mode: RotationMode,
  _angle?: number,
): void {
  const data = layer.pixels.data;
  const w = layer.pixels.width;
  const h = layer.pixels.height;

  let newW: number, newH: number;
  let rotated: Uint8ClampedArray;

  if (mode === "90-cw" || mode === "90-ccw" || mode === "180" || mode === "flip-h" || mode === "flip-v") {
    // Discrete, lossless reorientation — shared with TransformLayerOrientationCommand.
    const out = rotateOrientationBuffer(data, w, h, mode as OrientationOp);
    rotated = out.data;
    newW = out.width;
    newH = out.height;
  } else {
    // Arbitrary rotation: use bilinear interpolation
    const angle = (_angle ?? 0) * Math.PI / 180;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    // Calculate new bounding box
    const corners = [
      [0, 0], [w, 0], [w, h], [0, h],
    ].map(([x, y]) => {
      const cx = x! - w / 2;
      const cy = y! - h / 2;
      return [cx * cos - cy * sin + w / 2, cx * sin + cy * cos + h / 2];
    });

    const minX = Math.max(0, Math.floor(Math.min(...corners.map(c => c[0]!))));
    const minY = Math.max(0, Math.floor(Math.min(...corners.map(c => c[1]!))));
    const maxX = Math.min(w, Math.ceil(Math.max(...corners.map(c => c[0]!))));
    const maxY = Math.min(h, Math.ceil(Math.max(...corners.map(c => c[1]!))));

    newW = maxX - minX;
    newH = maxY - minY;
    rotated = new Uint8ClampedArray(newW * newH * 4);

    for (let dy = 0; dy < newH; dy++) {
      for (let dx = 0; dx < newW; dx++) {
        // Map destination pixel back to source
        const sx = (dx + minX - w / 2) * cos + (dy + minY - h / 2) * sin + w / 2;
        const sy = -(dx + minX - w / 2) * sin + (dy + minY - h / 2) * cos + h / 2;

        if (sx < 0 || sx >= w - 1 || sy < 0 || sy >= h - 1) {
          const di = (dy * newW + dx) * 4;
          rotated[di] = 0; rotated[di + 1] = 0; rotated[di + 2] = 0; rotated[di + 3] = 0;
          continue;
        }

        const x0 = Math.floor(sx);
        const y0 = Math.floor(sy);
        const x1 = x0 + 1;
        const y1 = y0 + 1;
        const tx = sx - x0;
        const ty = sy - y0;

        const i00 = (y0 * w + x0) * 4;
        const i10 = (y0 * w + x1) * 4;
        const i01 = (y1 * w + x0) * 4;
        const i11 = (y1 * w + x1) * 4;
        const di = (dy * newW + dx) * 4;

        for (let c = 0; c < 4; c++) {
          const top = data[i00 + c]! * (1 - tx) + data[i10 + c]! * tx;
          const bot = data[i01 + c]! * (1 - tx) + data[i11 + c]! * tx;
          rotated[di + c] = Math.round(top * (1 - ty) + bot * ty);
        }
      }
    }
  }

  layer.pixels = new PixelBuffer(newW, newH, "rgba8");
  layer.pixels.data.set(rotated);
  layer.pixels.markDirty();

  // Update layer position
  layer.x = 0;
  layer.y = 0;
}
